import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Save, Plus, Trash2, GripVertical } from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const blank = () => ({
  uid: crypto.randomUUID(),
  year: "",
  tag_ar: "",
  tag_en: "",
  title_ar: "",
  title_en: "",
  org_ar: "",
  org_en: "",
});
const withUid = (it) => (it && it.uid ? it : { ...it, uid: crypto.randomUUID() });

const AdminTimeline = () => {
  const [data, setData] = useState({ heading_ar: "", heading_en: "", sub_ar: "", sub_en: "", items: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    api
      .get("/admin/content/timeline")
      .then((r) => {
        const d = r.data?.data || {};
        setData({
          heading_ar: d.heading_ar || "",
          heading_en: d.heading_en || "",
          sub_ar: d.sub_ar || "",
          sub_en: d.sub_en || "",
          items: (d.items || []).map(withUid),
        });
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        heading_ar: data.heading_ar,
        heading_en: data.heading_en,
        sub_ar: data.sub_ar,
        sub_en: data.sub_en,
        items: data.items.map(({ uid, ...rest }) => rest),
      };
      await api.put("/admin/content/timeline", payload);
      toast.success("تم حفظ المشاريع بالترتيب الجديد");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const setItem = (uid, key, v) => setData((d) => ({
    ...d,
    items: d.items.map((it) => (it.uid === uid ? { ...it, [key]: v } : it)),
  }));
  const addItem = () => setData((d) => ({ ...d, items: [...d.items, blank()] }));
  const delItem = (uid) => setData((d) => ({ ...d, items: d.items.filter((it) => it.uid !== uid) }));

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setData((d) => {
      const oldIdx = d.items.findIndex((it) => it.uid === active.id);
      const newIdx = d.items.findIndex((it) => it.uid === over.id);
      return { ...d, items: arrayMove(d.items, oldIdx, newIdx) };
    });
  };

  if (loading) return <div className="overline animate-pulse">…</div>;

  return (
    <div data-testid="admin-timeline">
      <Toaster position="top-left" theme="dark" richColors />
      <div className="overline mb-3">محتوى الصفحة</div>
      <h1 className="font-arabic font-black text-3xl sm:text-4xl text-bone mb-4">قسم المشاريع</h1>
      <p className="text-white/50 text-sm mb-8 font-arabic">اسحب وأفلِت لإعادة ترتيب المشاريع.</p>

      <form onSubmit={save} className="space-y-6 max-w-5xl">
        <fieldset className="border border-white/10 bg-ink-soft p-6 space-y-5">
          <legend className="overline px-3">الترويسة</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <In label="العنوان (عربي)" v={data.heading_ar} on={(v) => setData((d) => ({ ...d, heading_ar: v }))} testId="timeline-heading-ar" />
            <In label="Heading (English)" v={data.heading_en} on={(v) => setData((d) => ({ ...d, heading_en: v }))} ltr testId="timeline-heading-en" />
            <In label="الوصف (عربي)" v={data.sub_ar} on={(v) => setData((d) => ({ ...d, sub_ar: v }))} testId="timeline-sub-ar" />
            <In label="Subtitle (English)" v={data.sub_en} on={(v) => setData((d) => ({ ...d, sub_en: v }))} ltr testId="timeline-sub-en" />
          </div>
        </fieldset>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={data.items.map((it) => it.uid)} strategy={verticalListSortingStrategy}>
            <div className="space-y-4">
              {data.items.map((it, i) => (
                <SortableItem key={it.uid} item={it} index={i} setItem={setItem} delItem={delItem} />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        <button type="button" onClick={addItem} className="inline-flex items-center gap-2 overline text-white/65 hover:text-gold" data-testid="timeline-add">
          <Plus size={12} /> إضافة مشروع
        </button>

        <div>
          <button type="submit" disabled={saving} className="btn-gold disabled:opacity-60" data-testid="timeline-save">
            <Save size={14} />
            {saving ? "جاري الحفظ…" : "حفظ"}
          </button>
        </div>
      </form>
    </div>
  );
};

const SortableItem = ({ item, index, setItem, delItem }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.uid });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} data-testid={`timeline-row-${index}`} className="border border-white/10 bg-ink-soft p-5 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="text-white/40 hover:text-gold cursor-grab active:cursor-grabbing touch-none"
            aria-label="drag"
            {...attributes}
            {...listeners}
            data-testid={`timeline-drag-${index}`}
          >
            <GripVertical size={16} />
          </button>
          <span className="overline">مشروع #{index + 1}</span>
        </div>
        <button type="button" onClick={() => delItem(item.uid)} className="text-white/50 hover:text-red-400" data-testid={`timeline-delete-${index}`}>
          <Trash2 size={14} />
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <In label="السنة" v={item.year} on={(v) => setItem(item.uid, "year", v)} ltr />
        <In label="التصنيف (عربي)" v={item.tag_ar} on={(v) => setItem(item.uid, "tag_ar", v)} />
        <In label="Tag (English)" v={item.tag_en} on={(v) => setItem(item.uid, "tag_en", v)} ltr />
        <div />
        <In label="العنوان (عربي)" v={item.title_ar} on={(v) => setItem(item.uid, "title_ar", v)} />
        <In label="Title (English)" v={item.title_en} on={(v) => setItem(item.uid, "title_en", v)} ltr />
        <In label="الجهة (عربي)" v={item.org_ar} on={(v) => setItem(item.uid, "org_ar", v)} />
        <In label="Org (English)" v={item.org_en} on={(v) => setItem(item.uid, "org_en", v)} ltr />
      </div>
    </div>
  );
};

const In = ({ label, v, on, ltr, testId }) => (
  <label className="block">
    <span className="block mb-2 text-xs tracking-[0.2em] uppercase text-white/50 font-arabic">{label}</span>
    <input
      type="text"
      value={v || ""}
      onChange={(e) => on(e.target.value)}
      className="editorial-input"
      dir={ltr ? "ltr" : "auto"}
      data-testid={testId}
    />
  </label>
);

export default AdminTimeline;
